/**
 * Marks the current SSR response as `410 Gone` (see `~/lib/shared/gone-response`
 * for why this is a header and not a status code).
 *
 * Lives outside `~/lib/server/` on purpose: route loaders run on the client too,
 * so this module has to stay importable from a route file. A static import of
 * `@tanstack/react-start/server` there would drag h3 and its Node stream
 * internals into the client bundle — the same edge `~/lib/request-context`
 * exists to avoid. A plain dynamic `import()` would not help either: it only
 * code-splits, and the module still ships as a lazy client chunk. Hence
 * `createServerOnlyFn`, which the compiler replaces with a thrower in the
 * client build, plus the `import.meta.env.SSR` guard so that thrower is
 * unreachable (Vite folds the constant, so the branch is eliminated outright).
 *
 * Aliases: mark gone, 410 loader, archived response
 */

import { createServerOnlyFn } from "@tanstack/react-start";

import { GONE_MARKER_HEADER } from "~/lib/shared/gone-response";

const importServerResponse = createServerOnlyFn(
  () => import("@tanstack/react-start/server"),
);

/**
 * Flag this request so the Worker returns `410 Gone` instead of `404`.
 *
 * Call it immediately before `throw notFound()` for a resource that was
 * deliberately withdrawn. No-op on the client, where the status of an already
 * sent document is not ours to change — a client-side navigation to an archived
 * page still renders the not-found UI, it just has no status code to set.
 */
export async function markResponseGone(): Promise<void> {
  if (!import.meta.env.SSR) return;
  try {
    const { setResponseHeader } = await importServerResponse();
    setResponseHeader(GONE_MARKER_HEADER, "1");
  } catch {
    // `getH3Event()` throws when there is no request in AsyncLocalStorage —
    // a prerender pass, a warmup, a future runtime that loads loaders outside
    // the request scope. Swallow it: the caller is on its way to `notFound()`
    // either way, and letting this escalate a 404 into a 500 would trade a
    // crawler hint for a real error page.
  }
}
